// Share to Twinden (docs/share-to-twinden.md, D177–D181): what the share
// sheet handed over, as the service worker stored it (public/sw-share.js,
// same database). Read by the /share page; deleted when it's sent or
// cancelled, and after a day if neither happens.
import { FILE_LIMITS } from '@twinden/shared';
import type { AttachmentKind } from './attachments';

const DB_NAME = 'twinden-shares';
const STORE = 'shares';
export const SHARE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface StoredShare {
  id: string;
  createdAt: number;
  title: string;
  text: string;
  url: string;
  files: Array<{ name: string; type: string; size: number; blob: Blob }>;
}

export interface Share {
  id: string;
  createdAt: number;
  title: string;
  text: string;
  url: string;
  files: File[];
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('No IndexedDB'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    // Same schema as sw-share.js: whichever opens it first creates it.
    request.onupgradeneeded = () => {
      const store = request.result.createObjectStore(STORE, { keyPath: 'id' });
      store.createIndex('createdAt', 'createdAt');
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function finished(tx: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** The share with this id, or null if it's gone (sent, cancelled, too old). */
export async function readShare(
  id: string,
  now = Date.now()
): Promise<Share | null> {
  const db = await open();
  try {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).get(id);
    await finished(tx);
    const stored = request.result as StoredShare | undefined;
    if (!stored || now - stored.createdAt > SHARE_MAX_AGE_MS) return null;
    return {
      id: stored.id,
      createdAt: stored.createdAt,
      title: stored.title,
      text: stored.text,
      url: stored.url,
      files: stored.files.map((file) =>
        file.blob instanceof File && file.blob.name === file.name
          ? file.blob
          : new File([file.blob], file.name, { type: file.type })
      ),
    };
  } finally {
    db.close();
  }
}

export async function deleteShare(id: string): Promise<void> {
  try {
    const db = await open();
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      await finished(tx);
    } finally {
      db.close();
    }
  } catch {
    // Nothing stored, or no IndexedDB: nothing to delete.
  }
}

/** Deletes every share stored more than a day ago (D179). */
export async function deleteOldShares(now = Date.now()): Promise<number> {
  try {
    const db = await open();
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const request = store
        .index('createdAt')
        .getAllKeys(IDBKeyRange.upperBound(now - SHARE_MAX_AGE_MS, true));
      let removed = 0;
      request.onsuccess = () => {
        for (const key of request.result) {
          store.delete(key);
          removed += 1;
        }
      };
      await finished(tx);
      return removed;
    } finally {
      db.close();
    }
  } catch {
    return 0;
  }
}

/**
 * The message a share's text fields make (D180). Apps fill them differently:
 * Chrome puts the page title in `title` and the link in `text`; others use
 * `url`. The link is added only if the text doesn't already have it, and the
 * title only when there's no text of its own.
 */
export function shareMessage(share: Pick<Share, 'title' | 'text' | 'url'>) {
  const text = share.text.trim();
  const url = share.url.trim();
  const title = share.title.trim();
  if (text) return url && !text.includes(url) ? `${text}\n${url}` : text;
  if (url) return title && title !== url ? `${title}\n${url}` : url;
  return title;
}

/** How a shared file is sent: the same kinds the composer uses. */
export function shareKind(file: Blob): AttachmentKind {
  if (file.type === 'image/gif') return 'gif';
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return 'file';
}

const DOCUMENT_TYPES: readonly string[] = FILE_LIMITS.ALLOWED_FILE_TYPES;

/**
 * Why a shared file can't be sent, or null when it can: the composer's types
 * (photos, videos, audio, PDF, Word, text) up to the upload limit.
 */
export function shareFileProblem(file: Blob): 'type' | 'size' | null {
  const type = file.type;
  const known =
    type.startsWith('image/') ||
    type.startsWith('video/') ||
    type.startsWith('audio/') ||
    DOCUMENT_TYPES.includes(type);
  if (!known) return 'type';
  if (file.size > FILE_LIMITS.MAX_SIZE) return 'size';
  return null;
}

// Signed out when the share arrived: the id waits here through sign-in
// (D178). localStorage can throw (private mode); then the share is lost.
const PENDING_KEY = 'twinden:pending-share';

export function rememberPendingShare(search: string) {
  try {
    localStorage.setItem(PENDING_KEY, search);
  } catch {
    // Not kept: the share page says the share is gone.
  }
}

/** The /share query string to go back to after signing in, if any. */
export function pendingShare(): string | null {
  try {
    return localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

/** The share page opened: nothing to go back to any more. */
export function clearPendingShare() {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    // Nothing kept.
  }
}

/**
 * Signing out (or deleting the account) forgets every share kept on this
 * device and the one waiting for sign-in, so the next person to sign in
 * here doesn't get it (D178).
 */
export async function forgetShares(): Promise<void> {
  clearPendingShare();
  try {
    const db = await open();
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      await finished(tx);
    } finally {
      db.close();
    }
  } catch {
    // No IndexedDB: nothing kept.
  }
}
